import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Users, Clock, CheckCircle, XCircle, Settings, Shield, Upload, ChevronRight, ChevronDown, AlertCircle, Loader2, Download, History, Search, Folder, FolderOpen, Eye, ChevronLeft, X, Calendar, Sparkles, Camera } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { generateAbsencePdf, generateAbsencePdfBase64 } from '../../lib/absencePdfGenerator';
import { useAuth } from '../../context/AuthContext';
import { format, parseISO, startOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CongesEmailSettingsModal } from './CongesEmailSettingsModal';

const api = (path: string, options?: RequestInit) => {
    const token = localStorage.getItem('token');
    return fetch(`/api${path}`, {
        ...options,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options?.headers }
    }).then(r => r.json());
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
    pending: { label: 'En attente', color: 'bg-warning-500/15 text-warning-400 border-warning-500/20' },
    accepted: { label: 'Acceptée', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' },
    refused: { label: 'Refusée', color: 'bg-red-500/15 text-red-400 border-red-500/20' },
};

interface Employee {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    position: string;
    job_post_title?: string;
    hire_date?: string;
    is_account_active: number;
    portal_password_temp: number;
    portal_password?: string;
    portal_username?: string;
    portal_signature_url?: string;
}

interface AbsenceRequest {
    id: string;
    employe_id: string;
    first_name: string;
    last_name: string;
    position?: string;
    type_code: string;
    date_start: string;
    date_end: string;
    days_count?: number;
    hire_date?: string;
    note: string;
    status: string;
    created_at: string;
    updated_at?: string;
}

interface RHSettings {
    company_name?: string;
    company_logo_url?: string;
    rh_allowed_absence_codes: string[];
    rh_signatory_name: string;
    rh_signature_url: string;
    legal?: {
        company_name?: string;
        address?: string;
        postal_code?: string;
        city?: string;
        country?: string;
        siret?: string;
        tva?: string;
        capital?: string;
        ape?: string;
        siege_social?: string;
        rcs_ville?: string;
        rcs_numero?: string;
        phone?: string;
    } | null;
}

type Tab = 'requests' | 'settings';
type RequestFilter = 'pending' | 'history';

export const RHModule: React.FC = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<Tab>('requests');
    const [showEmailSettings, setShowEmailSettings] = useState(false);
    const [requestFilter, setRequestFilter] = useState<RequestFilter>('pending');
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [requests, setRequests] = useState<AbsenceRequest[]>([]);
    const [settings, setSettings] = useState<RHSettings>({ rh_allowed_absence_codes: [], rh_signatory_name: '', rh_signature_url: '' });
    const [absenceCodes, setAbsenceCodes] = useState<{ code: string; color?: string }[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [processingId, setProcessingId] = useState<string | null>(null);
    const [editedDays, setEditedDays] = useState<Record<string, number>>({});
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
    const signatureInputRef = useRef<HTMLInputElement>(null);

    // Search & Folder States
    const [reqSearch, setReqSearch] = useState('');
    const [openYears, setOpenYears] = useState<Record<string, boolean>>({});
    const [openMonths, setOpenMonths] = useState<Record<string, boolean>>({});

    // Modal Visualiser le planning du salarié
    const [planningModalReq, setPlanningModalReq] = useState<AbsenceRequest | null>(null);
    const [planningModalWeekStart, setPlanningModalWeekStart] = useState<string>('');
    const [planningModalLoading, setPlanningModalLoading] = useState<boolean>(false);
    const [planningModalData, setPlanningModalData] = useState<any>(null);
    const [planningSettings, setPlanningSettings] = useState<any>(null);
    const [planningTemplates, setPlanningTemplates] = useState<any[]>([]);
    const [existingWeeks, setExistingWeeks] = useState<string[]>([]);

    // Digitize Signature Modal state
    const [showDigitizeModal, setShowDigitizeModal] = useState<boolean>(false);
    const [digitizeStep, setDigitizeStep] = useState<number>(1);
    const [digitizePhoto, setDigitizePhoto] = useState<string | null>(null);
    const [digitizeSubmitting, setDigitizeSubmitting] = useState<boolean>(false);

    const cameraInputRef = useRef<HTMLInputElement>(null);
    const digitizeFileInputRef = useRef<HTMLInputElement>(null);

    const handleDigitizeFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const raw = ev.target?.result as string;
            if (!raw) return;

            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const maxW = 1000;
                const maxH = 1000;
                let width = img.width;
                let height = img.height;

                if (width > maxW) {
                    height = Math.round((height * maxW) / width);
                    width = maxW;
                }
                if (height > maxH) {
                    width = Math.round((width * maxH) / height);
                    height = maxH;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(img, 0, 0, width, height);
                    setDigitizePhoto(canvas.toDataURL('image/jpeg', 0.85));
                }
            };
            img.src = raw;
        };
        reader.readAsDataURL(file);
    };

    const submitDigitizeRequest = async () => {
        if (!digitizePhoto) return;
        setDigitizeSubmitting(true);
        try {
            const r = await api('/rh/digitize-signature-request', {
                method: 'POST',
                body: JSON.stringify({
                    image_base64: digitizePhoto,
                    signatory_name: settings.rh_signatory_name
                })
            });
            setDigitizeSubmitting(false);
            if (r.error) {
                showToast(r.error, 'error');
            } else {
                setDigitizeStep(3);
                showToast('Demande transmise avec succès !');
            }
        } catch (e) {
            setDigitizeSubmitting(false);
            showToast('Erreur lors de la transmission', 'error');
        }
    };

    const openPlanningModal = async (req: AbsenceRequest) => {
        setPlanningModalReq(req);
        try {
            const d = parseISO(req.date_start);
            const monday = startOfWeek(d, { weekStartsOn: 1 });
            const mondayStr = format(monday, 'yyyy-MM-dd');
            setPlanningModalWeekStart(mondayStr);

            // Load existing weeks to check availability
            const weeksList = await api('/planning/weeks').catch(() => []);
            if (Array.isArray(weeksList)) {
                setExistingWeeks(weeksList.map((w: any) => w.week_start));
            }

            await loadModalPlanningWeek(mondayStr);
        } catch (e) {
            console.error('Failed to parse date for planning modal:', e);
        }
    };

    const loadModalPlanningWeek = async (mondayStr: string) => {
        setPlanningModalLoading(true);
        try {
            const [weekRes, settingsRes, templatesRes] = await Promise.all([
                api(`/planning/week/${mondayStr}`),
                planningSettings ? Promise.resolve(planningSettings) : api('/planning/settings').catch(() => ({})),
                planningTemplates.length > 0 ? Promise.resolve(planningTemplates) : api('/planning/templates').catch(() => [])
            ]);
            setPlanningModalData(weekRes);
            if (settingsRes && !settingsRes.error) {
                setPlanningSettings(settingsRes);
            }
            if (Array.isArray(templatesRes)) {
                setPlanningTemplates(templatesRes);
            }
        } catch (e) {
            console.error('Failed to load week for modal:', e);
            setPlanningModalData(null);
        } finally {
            setPlanningModalLoading(false);
        }
    };

    const changeModalWeek = (delta: number) => {
        if (!planningModalWeekStart) return;
        try {
            const current = parseISO(planningModalWeekStart);
            const next = delta > 0 ? addWeeks(current, delta) : subWeeks(current, Math.abs(delta));
            const nextStr = format(next, 'yyyy-MM-dd');
            setPlanningModalWeekStart(nextStr);
            loadModalPlanningWeek(nextStr);
        } catch (e) {
            console.error(e);
        }
    };

    const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const loadAll = async () => {
        setLoading(true);
        try {
            const [emps, reqs, rhS, planConfig] = await Promise.all([
                api('/employes'),
                api('/absence-requests'),
                api('/rh/settings'),
                api('/planning/settings').catch(() => ({}))
            ]);
            setEmployees(Array.isArray(emps) ? emps.filter((e: any) => !e.deleted_at) : []);
            setRequests(Array.isArray(reqs) ? reqs : []);
            if (rhS && !rhS.error) setSettings(rhS);
            const codes = Array.isArray(planConfig?.absenceCodes) ? planConfig.absenceCodes : [];
            setAbsenceCodes(codes.filter((c: any) => c.code !== 'REPOS'));
        } catch (e) {
            showToast('Erreur de chargement', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, []);

    const handleRequest = async (req: AbsenceRequest, status: 'accepted' | 'refused') => {
        if (status === 'accepted' && (!settings.rh_signatory_name?.trim() || !settings.rh_signature_url)) {
            showToast('Configurez un nom de signataire et une signature dans Paramètres avant de valider une demande.', 'error');
            setActiveTab('settings');
            return;
        }

        setProcessingId(req.id);

        const daysCountOverride = editedDays[req.id];
        const emp = employees.find(e => e.id === req.employe_id);
        const employeeName = `${req.first_name || emp?.first_name || ''} ${req.last_name || emp?.last_name || ''}`.trim();
        const finalDaysCount = daysCountOverride ?? req.days_count ?? 1;

        let pdfBase64: string | undefined;
        let pdfFileName: string | undefined;
        if (status === 'accepted') {
            try {
                const pdfResult = await generateAbsencePdfBase64({
                    id: req.id,
                    employeeName,
                    employeePosition: req.position || emp?.job_post_title || emp?.position,
                    employeeHireDate: req.hire_date || emp?.hire_date,
                    companyName: settings.company_name || settings.legal?.company_name || 'Votre Établissement',
                    companyLogoUrl: settings.company_logo_url,
                    typeCode: req.type_code,
                    dateStart: req.date_start,
                    dateEnd: req.date_end,
                    daysCount: finalDaysCount,
                    note: req.note,
                    status: 'accepted',
                    createdAt: req.created_at,
                    approvedAt: req.updated_at || new Date().toISOString(),
                    signatoryName: settings.rh_signatory_name,
                    signatureUrl: settings.rh_signature_url,
                    employeeSignatureUrl: emp?.portal_signature_url,
                    legal: settings.legal,
                    brandColor: user?.theme_color || undefined
                });
                if (pdfResult) {
                    pdfBase64 = pdfResult.base64;
                    pdfFileName = pdfResult.fileName;
                }
            } catch (pdfErr) {
                console.error('Error generating PDF:', pdfErr);
            }
        }

        setRequests(prev => prev.map(r => r.id === req.id ? { ...r, status, days_count: finalDaysCount } : r));

        const payload: Record<string, unknown> = { status };
        if (daysCountOverride != null) payload.days_count_override = daysCountOverride;
        if (pdfBase64) payload.pdf_base64 = pdfBase64;

        let r = await api(`/absence-requests/${req.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
        if (r?.error) {
            r = await api(`/absence-requests/${req.id}/status`, { method: 'POST', body: JSON.stringify(payload) });
        }

        if (r?.error) {
            setRequests(prev => prev.map(r => r.id === req.id ? { ...r, status: 'pending' } : r));
            showToast(r.error, 'error');
        } else {
            showToast(status === 'accepted' ? 'Absence acceptée — email + PDF envoyés !' : 'Demande refusée');

            if (status === 'accepted' && pdfBase64 && req.employe_id && pdfFileName) {
                try {
                    await api(`/employes/${req.employe_id}/documents/save-pdf`, {
                        method: 'POST',
                        body: JSON.stringify({
                            display_name: `Autorisation Absence – ${req.type_code} (${formatDate(req.date_start)} au ${formatDate(req.date_end)})`,
                            file_name: pdfFileName,
                            base64: pdfBase64,
                            absence_request_id: req.id
                        })
                    });
                } catch (pdfErr) {
                    console.error('Error auto-saving PDF to employee documents:', pdfErr);
                }
            }
        }

        setEditedDays(prev => { const n = { ...prev }; delete n[req.id]; return n; });
        setProcessingId(null);
        setRequestFilter('history');
        
        // Dispatch custom event to update the sidebar badge instantly
        window.dispatchEvent(new Event('absenceRequestProcessed'));
    };

    const saveSettings = async () => {
        setSaving(true);
        const r = await api('/rh/settings', { method: 'PUT', body: JSON.stringify(settings) });
        setSaving(false);
        if (r.error) showToast(r.error, 'error');
        else showToast('Paramètres enregistrés');
    };

    const handleSignatureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (ev) => {
            const rawDataUrl = ev.target?.result as string;
            if (!rawDataUrl) return;

            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const maxW = 500;
                const maxH = 250;
                let width = img.width;
                let height = img.height;

                if (width > maxW) {
                    height = Math.round((height * maxW) / width);
                    width = maxW;
                }
                if (height > maxH) {
                    width = Math.round((width * maxH) / height);
                    height = maxH;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.clearRect(0, 0, width, height);
                    ctx.drawImage(img, 0, 0, width, height);
                    const compressedDataUrl = canvas.toDataURL('image/png');
                    setSettings(s => ({ ...s, rh_signature_url: compressedDataUrl }));
                }
            };
            img.src = rawDataUrl;
        };
        reader.readAsDataURL(file);
    };

    const toggleAllowedCode = (code: string) => {
        setSettings(s => {
            const allowed = Array.isArray(s?.rh_allowed_absence_codes) ? s.rh_allowed_absence_codes : [];
            const already = allowed.includes(code);
            return { ...s, rh_allowed_absence_codes: already ? allowed.filter(c => c !== code) : [...allowed, code] };
        });
    };

    // Filtered Lists
    const pendingRequests = useMemo(() => requests.filter(r => r.status === 'pending'), [requests]);
    const historyRequests = useMemo(() => requests.filter(r => r.status !== 'pending'), [requests]);

    const filteredPendingRequests = useMemo(() => {
        if (!reqSearch.trim()) return pendingRequests;
        const q = reqSearch.toLowerCase();
        return pendingRequests.filter(r =>
            `${r.first_name || ''} ${r.last_name || ''}`.toLowerCase().includes(q) ||
            (r.type_code && r.type_code.toLowerCase().includes(q)) ||
            (r.date_start && r.date_start.includes(q)) ||
            (r.date_end && r.date_end.includes(q)) ||
            (r.note && r.note.toLowerCase().includes(q))
        );
    }, [pendingRequests, reqSearch]);

    const filteredHistoryRequests = useMemo(() => {
        if (!reqSearch.trim()) return historyRequests;
        const q = reqSearch.toLowerCase();
        return historyRequests.filter(r =>
            `${r.first_name || ''} ${r.last_name || ''}`.toLowerCase().includes(q) ||
            (r.type_code && r.type_code.toLowerCase().includes(q)) ||
            (r.date_start && r.date_start.includes(q)) ||
            (r.date_end && r.date_end.includes(q)) ||
            (r.note && r.note.toLowerCase().includes(q))
        );
    }, [historyRequests, reqSearch]);

    // Grouped History by Year -> Month
    const groupedHistory = useMemo(() => {
        const yearsMap: Record<string, Record<string, { monthName: string; items: AbsenceRequest[] }>> = {};

        filteredHistoryRequests.forEach(req => {
            let year = '2026';
            let monthKey = '07';
            let monthName = 'Juillet';
            try {
                const dateObj = parseISO(req.date_start || req.created_at);
                year = format(dateObj, 'yyyy');
                monthKey = format(dateObj, 'MM');
                monthName = format(dateObj, 'MMMM', { locale: fr });
                monthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
            } catch {}

            if (!yearsMap[year]) yearsMap[year] = {};
            if (!yearsMap[year][monthKey]) {
                yearsMap[year][monthKey] = { monthName, items: [] };
            }
            yearsMap[year][monthKey].items.push(req);
        });

        return yearsMap;
    }, [filteredHistoryRequests]);

    // Initialize all years & months as open by default if state is empty
    useEffect(() => {
        const yearsKeys = Object.keys(groupedHistory);
        if (yearsKeys.length > 0) {
            setOpenYears(prev => {
                const next = { ...prev };
                yearsKeys.forEach(yr => { if (next[yr] === undefined) next[yr] = true; });
                return next;
            });
            setOpenMonths(prev => {
                const next = { ...prev };
                yearsKeys.forEach(yr => {
                    Object.keys(groupedHistory[yr] || {}).forEach(mKey => {
                        const fullKey = `${yr}-${mKey}`;
                        if (next[fullKey] === undefined) next[fullKey] = true;
                    });
                });
                return next;
            });
        }
    }, [groupedHistory]);

    const toggleYear = (yr: string) => setOpenYears(prev => ({ ...prev, [yr]: !prev[yr] }));
    const toggleMonth = (fullKey: string) => setOpenMonths(prev => ({ ...prev, [fullKey]: !prev[fullKey] }));

    const tabs: { id: Tab; label: string; icon: React.ReactNode; badge?: number }[] = [
        { id: 'requests', label: 'Demandes d\'Absence', icon: <Clock size={16} />, badge: pendingRequests.length },
        { id: 'settings', label: 'Paramètre', icon: <Settings size={16} /> },
    ];

    const formatDate = (d: string) => {
        try { return format(parseISO(d), 'dd/MM/yyyy', { locale: fr }); } catch { return d; }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={32} />
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Toast */}
            <AnimatePresence>
                {toast && (
                    <motion.div
                        initial={{ opacity: 0, y: -20, x: '50%' }}
                        animate={{ opacity: 1, y: 0, x: '50%' }}
                        exit={{ opacity: 0, y: -20, x: '50%' }}
                        className={`fixed top-6 right-1/2 z-50 px-5 py-3 rounded-2xl font-bold text-sm flex items-center gap-2 shadow-2xl border ${toast.type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-red-500/20 text-red-300 border-red-500/30'}`}
                    >
                        {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                        {toast.msg}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Header */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-600 dark:text-brand-400">
                        <Users size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-content">Demande de congé(s)</h1>
                        <p className="text-sm text-content-muted">Gérez les accès de vos salariés et les demandes d'absences</p>
                    </div>
                </div>
                <button onClick={() => setShowEmailSettings(true)} className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all">
                    <Settings size={14} />
                    Paramètres
                </button>
            </div>
            {showEmailSettings && <CongesEmailSettingsModal onClose={() => setShowEmailSettings(false)} />}

            {/* Tabs */}
            <div className="flex gap-2 bg-surface-soft p-1.5 rounded-2xl border border-border w-fit">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${activeTab === tab.id ? 'bg-brand-600 text-brand-fg shadow-sm' : 'text-content-muted hover:bg-brand-600/10 hover:text-brand-600 dark:hover:text-brand-400'}`}
                    >
                        {tab.icon}
                        {tab.label}
                        {tab.badge !== undefined && tab.badge > 0 && (
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                                tab.id === 'requests'
                                    ? 'bg-red-500 text-content shadow-lg shadow-red-500/40 animate-pulse'
                                    : activeTab === tab.id ? 'bg-white/20 text-content' : 'bg-brand-500/10 text-brand-600 dark:text-brand-400 dark:text-brand-500 dark:text-brand-600 dark:text-brand-400'
                            }`}>
                                {tab.badge}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Warning Banner */}
            {(!settings.rh_allowed_absence_codes || settings.rh_allowed_absence_codes.length === 0) && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-2xl bg-warning-500/10 border border-warning-500/20 text-warning-300 text-sm flex items-start gap-3 shadow-lg">
                    <AlertCircle size={20} className="shrink-0 mt-0.5 text-warning-400" />
                    <div>
                        <p className="font-bold text-warning-200 mb-0.5">Attention : Aucun code d'absence n'est actuellement autorisé</p>
                        <p className="text-xs text-warning-300/80">
                            Vos salariés ne peuvent pas effectuer de demandes d'absence tant qu'aucun code n'est activé. Rendez-vous dans l'onglet <strong className="text-content">Paramètre</strong> et cochez au moins un code d'absence autorisé.
                        </p>
                    </div>
                </motion.div>
            )}

            {/* TAB: Demandes d'Absence */}
            {activeTab === 'requests' && (
                <div className="space-y-4">
                    {/* Search Bar & Sub-filter bar */}
                    <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
                        <div className="flex gap-2">
                            <button
                                onClick={() => setRequestFilter('pending')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${requestFilter === 'pending' ? 'bg-warning-500/20 text-warning-300 border border-warning-500/30' : 'bg-surface-soft text-content-muted hover:text-content border border-border'}`}
                            >
                                <Clock size={14} /> En attente
                                {pendingRequests.length > 0 && (
                                    <span className="bg-warning-500/30 text-warning-300 px-1.5 py-0.5 rounded-full text-[10px] font-black">{pendingRequests.length}</span>
                                )}
                            </button>
                            <button
                                onClick={() => setRequestFilter('history')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${requestFilter === 'history' ? 'bg-brand-500/10 text-brand-600 dark:text-brand-400 dark:text-brand-500 dark:text-brand-600 dark:text-brand-400 border border-brand-500/30' : 'bg-surface-soft text-content-muted hover:text-content border border-border'}`}
                            >
                                <History size={14} /> Historique
                                {historyRequests.length > 0 && (
                                    <span className="bg-brand-500/30 text-brand-500 dark:text-brand-600 dark:text-brand-400 px-1.5 py-0.5 rounded-full text-[10px] font-black">{historyRequests.length}</span>
                                )}
                            </button>
                        </div>

                        {/* Search input for requests */}
                        <div className="relative flex-1 max-w-md">
                            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-content-muted" />
                            <input
                                type="text"
                                value={reqSearch}
                                onChange={e => setReqSearch(e.target.value)}
                                placeholder="Rechercher (nom salarié, type CP, dates...)"
                                className="w-full bg-surface-soft border border-border rounded-xl pl-10 pr-4 py-2 text-content text-xs outline-none focus:border-brand-500/50 transition-all"
                            />
                        </div>
                    </div>

                    {/* Pending requests */}
                    {requestFilter === 'pending' && (
                        <div className="space-y-3">
                            {filteredPendingRequests.length === 0 && (
                                <div className="text-center py-16 text-content-muted border border-dashed border-border rounded-2xl">
                                    <CheckCircle size={40} className="mx-auto mb-3 opacity-30 text-emerald-400" />
                                    <p className="font-medium">{reqSearch ? 'Aucune demande en attente ne correspond' : 'Aucune demande en attente'}</p>
                                    {!reqSearch && <p className="text-sm">Toutes les demandes ont été traitées</p>}
                                </div>
                            )}
                            {filteredPendingRequests.map(req => {
                                const isProcessing = processingId === req.id;
                                const currentDays = editedDays[req.id] ?? req.days_count ?? 1;
                                return (
                                    <motion.div key={req.id} layout className="p-4 bg-surface-soft rounded-2xl border border-border space-y-3">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <p className="font-bold text-content text-sm">{req.first_name} {req.last_name}</p>
                                                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-lg border ${STATUS_LABELS.pending.color}`}>En attente</span>
                                                </div>
                                                <p className="text-xs text-content-muted">
                                                    <span className="font-bold text-brand-600 dark:text-brand-400">{req.type_code}</span>
                                                    {' '}· Du <span className="text-content font-semibold">{formatDate(req.date_start)}</span> au <span className="text-content font-semibold">{formatDate(req.date_end)}</span>
                                                </p>
                                                {req.note && <p className="text-xs text-content-muted mt-1 italic">"{req.note}"</p>}
                                                <p className="text-[11px] text-content-muted mt-1">Soumise le {formatDate(req.created_at)}</p>
                                            </div>
                                            {isProcessing ? (
                                                <Loader2 size={20} className="animate-spin text-brand-600 dark:text-brand-400 shrink-0 mt-1" />
                                            ) : (
                                                <div className="flex gap-2 shrink-0 flex-wrap">
                                                    <button onClick={() => openPlanningModal(req)} className="flex items-center gap-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-xs font-bold px-3 py-2 rounded-xl transition-all border border-blue-500/20">
                                                        <Eye size={14} /> Visualiser le planning
                                                    </button>
                                                    <button onClick={() => handleRequest(req, 'refused')} className="flex items-center gap-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold px-3 py-2 rounded-xl transition-all border border-red-500/20">
                                                        <XCircle size={14} /> Refuser
                                                    </button>
                                                    <button onClick={() => handleRequest(req, 'accepted')} className="flex items-center gap-1 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-bold px-3 py-2 rounded-xl transition-all border border-emerald-500/20">
                                                        <CheckCircle size={14} /> Accepter
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {/* Editable days count */}
                                        <div className="flex items-center gap-3 pt-2 border-t border-border">
                                            <span className="text-xs text-content-muted font-semibold">Jours d'absence :</span>
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => setEditedDays(prev => ({ ...prev, [req.id]: Math.max(0.5, (prev[req.id] ?? req.days_count ?? 1) - 0.5) }))} className="w-6 h-6 rounded-lg bg-surface-soft hover:bg-slate-200 dark:bg-surface-soft text-content-muted hover:text-content text-xs font-bold transition-all flex items-center justify-center">−</button>
                                                <input
                                                    type="number"
                                                    min={0.5}
                                                    step={0.5}
                                                    value={currentDays}
                                                    onChange={e => setEditedDays(prev => ({ ...prev, [req.id]: parseFloat(e.target.value) || 1 }))}
                                                    className="w-16 text-center bg-surface-soft border border-border rounded-lg px-2 py-1 text-content text-sm font-bold outline-none focus:border-brand-500/50"
                                                />
                                                <button onClick={() => setEditedDays(prev => ({ ...prev, [req.id]: (prev[req.id] ?? req.days_count ?? 1) + 0.5 }))} className="w-6 h-6 rounded-lg bg-surface-soft hover:bg-slate-200 dark:bg-surface-soft text-content-muted hover:text-content text-xs font-bold transition-all flex items-center justify-center">+</button>
                                                <span className="text-xs text-content-muted">jour(s)</span>
                                                {editedDays[req.id] != null && editedDays[req.id] !== req.days_count && (
                                                    <span className="text-[10px] text-warning-400 font-bold bg-warning-500/10 px-2 py-0.5 rounded-lg border border-warning-500/20">Modifié</span>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}

                    {/* History Organized by Folders (Year -> Month) */}
                    {requestFilter === 'history' && (
                        <div className="space-y-4">
                            {filteredHistoryRequests.length === 0 && (
                                <div className="text-center py-16 text-content-muted border border-dashed border-border rounded-2xl">
                                    <History size={40} className="mx-auto mb-3 opacity-30" />
                                    <p className="font-medium">{reqSearch ? 'Aucune demande trouvée pour cette recherche' : 'Aucun historique'}</p>
                                </div>
                            )}

                            {Object.keys(groupedHistory).sort((a, b) => b.localeCompare(a)).map(year => {
                                const monthsObj = groupedHistory[year];
                                const isYearOpen = openYears[year] ?? true;
                                const totalYearItems = Object.values(monthsObj).reduce((sum, m) => sum + m.items.length, 0);

                                return (
                                    <div key={year} className="space-y-2 border border-border rounded-3xl p-4 bg-surface-soft">
                                        {/* Year Folder Header */}
                                        <button
                                            onClick={() => toggleYear(year)}
                                            className="w-full flex items-center justify-between p-3 rounded-2xl bg-warning-500/10 hover:bg-warning-500/15 border border-warning-500/20 transition-all text-left"
                                        >
                                            <div className="flex items-center gap-3">
                                                {isYearOpen ? <FolderOpen className="text-warning-400" size={20} /> : <Folder className="text-warning-400" size={20} />}
                                                <span className="font-black text-content text-base">Année {year}</span>
                                                <span className="text-xs font-bold text-warning-300/80 bg-warning-500/20 px-2 py-0.5 rounded-full border border-warning-500/30">
                                                    {totalYearItems} demande{totalYearItems > 1 ? 's' : ''}
                                                </span>
                                            </div>
                                            {isYearOpen ? <ChevronDown className="text-warning-400" size={18} /> : <ChevronRight className="text-warning-400" size={18} />}
                                        </button>

                                        {/* Month Folders inside Year */}
                                        <AnimatePresence>
                                            {isYearOpen && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="space-y-3 pl-3 pt-2">
                                                    {Object.keys(monthsObj).sort((a, b) => b.localeCompare(a)).map(mKey => {
                                                        const monthData = monthsObj[mKey];
                                                        const fullMonthKey = `${year}-${mKey}`;
                                                        const isMonthOpen = openMonths[fullMonthKey] ?? true;

                                                        return (
                                                            <div key={fullMonthKey} className="space-y-2 border border-border rounded-2xl p-3 bg-surface">
                                                                {/* Month Folder Header */}
                                                                <button
                                                                    onClick={() => toggleMonth(fullMonthKey)}
                                                                    className="w-full flex items-center justify-between p-2.5 rounded-xl bg-brand-500/10 hover:bg-brand-500/15 border border-brand-500/20 transition-all text-left"
                                                                >
                                                                    <div className="flex items-center gap-2.5">
                                                                        {isMonthOpen ? <FolderOpen className="text-brand-600 dark:text-brand-400" size={18} /> : <Folder className="text-brand-600 dark:text-brand-400" size={18} />}
                                                                        <span className="font-bold text-content text-sm">{monthData.monthName}</span>
                                                                        <span className="text-[11px] font-bold text-brand-500 dark:text-brand-600 dark:text-brand-400/80 bg-brand-500/20 px-2 py-0.5 rounded-full border border-brand-500/30">
                                                                            {monthData.items.length}
                                                                        </span>
                                                                    </div>
                                                                    {isMonthOpen ? <ChevronDown className="text-brand-600 dark:text-brand-400" size={16} /> : <ChevronRight className="text-brand-600 dark:text-brand-400" size={16} />}
                                                                </button>

                                                                {/* Absence Requests Cards inside Month */}
                                                                <AnimatePresence>
                                                                    {isMonthOpen && (
                                                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="space-y-2 pl-2 pt-2">
                                                                            {monthData.items.map(req => {
                                                                                const s = STATUS_LABELS[req.status] || STATUS_LABELS.pending;
                                                                                const isAccepted = req.status === 'accepted' || req.status === 'approved';
                                                                                const emp = employees.find(e => e.id === req.employe_id);
                                                                                return (
                                                                                    <motion.div key={req.id} layout className="p-4 bg-surface-soft hover:bg-surface rounded-2xl border border-border transition-all">
                                                                                        <div className="flex items-center justify-between gap-4">
                                                                                            <div className="flex-1">
                                                                                                <div className="flex items-center gap-2 mb-1">
                                                                                                    <p className="font-bold text-content text-sm">{req.first_name} {req.last_name}</p>
                                                                                                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-lg border ${s.color}`}>{s.label}</span>
                                                                                                </div>
                                                                                                <p className="text-xs text-content-muted">
                                                                                                    <span className="font-bold text-brand-600 dark:text-brand-400">{req.type_code}</span>
                                                                                                    {' '}· Du <span className="text-content font-semibold">{formatDate(req.date_start)}</span> au <span className="text-content font-semibold">{formatDate(req.date_end)}</span>
                                                                                                    {req.days_count ? <span className="text-content-muted"> · {req.days_count} jour(s)</span> : ''}
                                                                                                </p>
                                                                                                {req.note && <p className="text-xs text-content-muted mt-1 italic">"{req.note}"</p>}
                                                                                                <p className="text-[11px] text-content-muted mt-1">Soumise le {formatDate(req.created_at)}</p>
                                                                                            </div>
                                                                                            <div className="flex items-center gap-2 shrink-0 flex-wrap">
                                                                                                <button
                                                                                                    onClick={() => openPlanningModal(req)}
                                                                                                    className="flex items-center gap-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-xs font-bold px-3 py-2 rounded-xl transition-all border border-blue-500/20 whitespace-nowrap"
                                                                                                >
                                                                                                    <Eye size={14} /> Planning
                                                                                                </button>
                                                                                                {isAccepted && (
                                                                                                    <button
                                                                                                        onClick={() => generateAbsencePdf({
                                                                                                            id: req.id,
                                                                                                            employeeName: `${req.first_name || ''} ${req.last_name || ''}`.trim(),
                                                                                                            employeePosition: req.position || emp?.job_post_title || emp?.position,
                                                                                                            employeeHireDate: req.hire_date || emp?.hire_date,
                                                                                                            companyName: settings.company_name || settings.legal?.company_name || 'Votre Établissement',
                                                                                                            companyLogoUrl: settings.company_logo_url,
                                                                                                            typeCode: req.type_code,
                                                                                                            dateStart: req.date_start,
                                                                                                            dateEnd: req.date_end,
                                                                                                            daysCount: req.days_count,
                                                                                                            note: req.note,
                                                                                                            status: 'accepted',
                                                                                                            createdAt: req.created_at,
                                                                                                            approvedAt: req.updated_at || req.created_at,
                                                                                                            signatoryName: settings.rh_signatory_name,
                                                                                                            signatureUrl: settings.rh_signature_url,
                                                                                                            employeeSignatureUrl: emp?.portal_signature_url,
                                                                                                            legal: settings.legal,
                                                                                                            brandColor: user?.theme_color || undefined
                                                                                                        })}
                                                                                                        className="flex items-center gap-1.5 bg-brand-500/10 hover:bg-brand-500/10 text-brand-600 dark:text-brand-400 dark:text-brand-500 dark:text-brand-600 dark:text-brand-400 text-xs font-bold px-3 py-2 rounded-xl transition-all border border-brand-500/20 whitespace-nowrap"
                                                                                                    >
                                                                                                        <Download size={14} /> Télécharger PDF
                                                                                                    </button>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    </motion.div>
                                                                                );
                                                                            })}
                                                                        </motion.div>
                                                                    )}
                                                                </AnimatePresence>
                                                            </div>
                                                        );
                                                    })}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* TAB: Paramètre */}
            {activeTab === 'settings' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Codes d'absence autorisés */}
                    <section className="bg-white/[0.03] rounded-3xl border border-border p-6">
                        <h3 className="font-bold text-content mb-1 flex items-center gap-2"><Clock size={16} className="text-brand-600 dark:text-brand-400" /> Codes d'absence autorisés</h3>
                        <p className="text-xs text-content-muted mb-4">Sélectionnez les types d'absences que vos salariés peuvent demander</p>
                        {absenceCodes.length === 0 && (
                            <p className="text-sm text-content-muted italic text-center py-6">Aucun code configuré par l'administrateur</p>
                        )}
                        <div className="space-y-2">
                            {absenceCodes.map((code: any) => {
                                const codeStr = typeof code === 'string' ? code : code.code;
                                const isAllowed = Array.isArray(settings?.rh_allowed_absence_codes) && settings.rh_allowed_absence_codes.includes(codeStr);
                                return (
                                    <button key={codeStr} onClick={() => toggleAllowedCode(codeStr)}
                                        className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${isAllowed ? 'bg-brand-500/10 border-brand-500/30 text-brand-500 font-bold' : 'bg-surface-soft border-border text-content-muted hover:border-border'}`}>
                                        <span className="font-bold text-sm">{codeStr}</span>
                                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isAllowed ? 'bg-brand-500 border-brand-500' : 'border-gray-600'}`}>
                                            {isAllowed && <CheckCircle size={10} className="text-white dark:text-white" />}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    {/* Signature */}
                    <section className="bg-white/[0.03] rounded-3xl border border-border p-6 space-y-4">
                        <h3 className="font-bold text-content mb-1 flex items-center gap-2"><Shield size={16} className="text-brand-600 dark:text-brand-400" /> Signature du Responsable</h3>
                        <p className="text-xs text-content-muted">Apparaît sur les PDF d'autorisation d'absence envoyés aux salariés</p>

                        <div>
                            <label className="text-xs text-content-muted font-semibold mb-1.5 block">Nom du signataire</label>
                            <input
                                type="text"
                                value={settings.rh_signatory_name}
                                onChange={e => setSettings(s => ({ ...s, rh_signatory_name: e.target.value }))}
                                placeholder="Ex: M. Jean Dupont, Directeur"
                                className="w-full bg-surface-soft border border-border rounded-xl px-4 py-3 text-content outline-none focus:border-brand-500/50 transition-all text-sm"
                            />
                        </div>

                        <div>
                            <label className="text-xs text-content-muted font-semibold mb-1.5 block">Image de signature (fond blanc)</label>
                            {settings.rh_signature_url && (
                                <div className="mb-3 p-3 bg-white rounded-xl">
                                    <img src={settings.rh_signature_url} alt="Signature" className="max-h-20 object-contain" />
                                </div>
                            )}
                            <input ref={signatureInputRef} type="file" accept="image/*" className="hidden" onChange={handleSignatureUpload} />
                            <button onClick={() => signatureInputRef.current?.click()}
                                className="flex items-center gap-2 w-full bg-surface-soft hover:bg-slate-200 dark:bg-surface-soft border border-dashed border-slate-300 dark:border-border hover:border-brand-500/40 text-content-muted hover:text-content py-3 rounded-xl text-sm font-semibold transition-all justify-center">
                                <Upload size={16} /> {settings.rh_signature_url ? 'Changer la signature' : 'Uploader une image'}
                            </button>
                        </div>

                        <button onClick={() => { setShowDigitizeModal(true); setDigitizeStep(1); setDigitizePhoto(null); }}
                            className="flex items-center gap-2 w-full bg-brand-500/10 hover:bg-brand-500/20 border border-brand-500/20 hover:border-brand-500/40 text-brand-500 dark:text-brand-600 dark:text-brand-400 hover:text-brand-200 py-3 rounded-xl text-sm font-semibold transition-all justify-center">
                            <Sparkles size={16} /> Soumettre une demande pour numériser ma signature
                        </button>

                        <button onClick={saveSettings} disabled={saving}
                            className="w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-brand-fg font-bold py-3 rounded-xl transition-all">
                            {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                            Enregistrer les paramètres
                        </button>
                    </section>
                </div>
            )}

            {/* Modal - Numériser la signature manuscrite */}
            <AnimatePresence>
                {showDigitizeModal && (
                    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-slate-900 border border-border rounded-3xl p-6 max-w-lg w-full shadow-2xl space-y-5">
                            <div className="flex items-center justify-between border-b border-border pb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-2xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-600 dark:text-brand-400">
                                        <Sparkles size={20} />
                                    </div>
                                    <h3 className="text-lg font-bold text-content">Numériser ma signature</h3>
                                </div>
                                <button onClick={() => setShowDigitizeModal(false)} className="p-2 text-content-muted hover:text-content rounded-xl hover:bg-surface-soft transition-all"><X size={20} /></button>
                            </div>

                            {digitizeStep === 1 && (
                                <div className="space-y-4">
                                    <p className="text-sm text-content-muted">Pour obtenir une signature numérisée de qualité professionnelle, merci de suivre ces étapes :</p>
                                    <ul className="space-y-3 text-sm text-content-muted">
                                        <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-brand-500/15 text-brand-500 dark:text-brand-600 dark:text-brand-400 text-xs font-bold flex items-center justify-center shrink-0">1</span> <span>Prenez une feuille de papier <b className="text-content">blanche et vierge</b>.</span></li>
                                        <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-brand-500/15 text-brand-500 dark:text-brand-600 dark:text-brand-400 text-xs font-bold flex items-center justify-center shrink-0">2</span> <span>Signez au <b className="text-content">stylo noir</b>, bien lisiblement.</span></li>
                                        <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-brand-500/15 text-brand-500 dark:text-brand-600 dark:text-brand-400 text-xs font-bold flex items-center justify-center shrink-0">3</span> <span>Prenez la photo <b className="text-content">avec le flash</b>, en cadrant bien la signature : ni trop loin, ni trop près.</span></li>
                                    </ul>
                                    <button onClick={() => setDigitizeStep(2)} className="w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 text-brand-fg font-bold py-3 rounded-xl transition-all">
                                        Suivant <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}

                            {digitizeStep === 2 && (
                                <div className="space-y-4">
                                    {!digitizePhoto ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleDigitizeFileChange} />
                                            <button onClick={() => cameraInputRef.current?.click()} className="flex flex-col items-center gap-2 bg-surface-soft hover:bg-slate-200 dark:bg-surface-soft border border-dashed border-slate-300 dark:border-border hover:border-brand-500/40 text-content-muted hover:text-content py-6 rounded-xl text-sm font-semibold transition-all">
                                                <Camera size={22} /> Appareil photo
                                            </button>
                                            <input ref={digitizeFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleDigitizeFileChange} />
                                            <button onClick={() => digitizeFileInputRef.current?.click()} className="flex flex-col items-center gap-2 bg-surface-soft hover:bg-slate-200 dark:bg-surface-soft border border-dashed border-slate-300 dark:border-border hover:border-brand-500/40 text-content-muted hover:text-content py-6 rounded-xl text-sm font-semibold transition-all">
                                                <Upload size={22} /> Charger une image
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className="p-3 bg-white rounded-xl">
                                                <img src={digitizePhoto} alt="Aperçu signature" className="max-h-40 w-full object-contain" />
                                            </div>
                                            <button onClick={() => setDigitizePhoto(null)} className="w-full text-xs text-content-muted hover:text-content transition-all">Reprendre une autre photo</button>
                                        </div>
                                    )}

                                    <div className="flex gap-3">
                                        <button onClick={() => setDigitizeStep(1)} className="flex-1 flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold py-3 rounded-xl transition-colors shadow-sm">
                                            <ChevronLeft size={16} /> Retour
                                        </button>
                                        <button onClick={submitDigitizeRequest} disabled={!digitizePhoto || digitizeSubmitting}
                                            className="flex-1 flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-brand-fg font-bold py-3 rounded-xl transition-all">
                                            {digitizeSubmitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                                            Soumettre ma demande
                                        </button>
                                    </div>
                                </div>
                            )}

                            {digitizeStep === 3 && (
                                <div className="space-y-4 text-center py-4">
                                    <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                                        <CheckCircle size={28} />
                                    </div>
                                    <p className="text-content font-bold">Demande envoyée !</p>
                                    <p className="text-sm text-content-muted">Votre demande de numérisation a bien été transmise au service concerné. Vous recevrez votre signature numérisée sous peu.</p>
                                    <button onClick={() => setShowDigitizeModal(false)} className="w-full bg-brand-600 hover:bg-brand-500 text-brand-fg font-bold py-3 rounded-xl transition-all">
                                        Fermer
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Modal Visualiser le planning du salarié */}
            {planningModalReq && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-slate-900 border border-border rounded-3xl p-4 md:p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl space-y-6">
                        <div className="flex items-center justify-between border-b border-border pb-4 flex-wrap gap-3">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                                    <Eye size={20} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white">Planning de {planningModalReq.first_name} {planningModalReq.last_name}</h3>
                                    <p className="text-xs text-white/60">
                                        Demande <span className="font-bold text-brand-600 dark:text-brand-400">{planningModalReq.type_code}</span> du {formatDate(planningModalReq.date_start)} au {formatDate(planningModalReq.date_end)} ({planningModalReq.days_count ?? 1} jour(s))
                                    </p>
                                </div>
                            </div>
                            <button onClick={() => setPlanningModalReq(null)} className="p-2 text-white/60 hover:text-white rounded-xl hover:bg-white/10 transition-all"><X size={20} /></button>
                        </div>

                        {/* Navigation Semaine & Pills */}
                        {(() => {
                            const currentMonday = planningModalWeekStart ? parseISO(planningModalWeekStart) : new Date();
                            const prevMondayStr = planningModalWeekStart ? format(subWeeks(currentMonday, 1), 'yyyy-MM-dd') : '';
                            const nextMondayStr = planningModalWeekStart ? format(addWeeks(currentMonday, 1), 'yyyy-MM-dd') : '';

                            const hasPrevWeek = existingWeeks.length === 0 || existingWeeks.includes(prevMondayStr);
                            const hasNextWeek = existingWeeks.length === 0 || existingWeeks.includes(nextMondayStr);
                            const isCurrentWeekCreated = existingWeeks.length === 0 || existingWeeks.includes(planningModalWeekStart);

                            let reqWeekPills: { dateStr: string; label: string }[] = [];
                            try {
                                let curr = startOfWeek(parseISO(planningModalReq.date_start), { weekStartsOn: 1 });
                                const endW = startOfWeek(parseISO(planningModalReq.date_end), { weekStartsOn: 1 });
                                let weekNum = 1;
                                while (curr <= endW) {
                                    const wStr = format(curr, 'yyyy-MM-dd');
                                    const endCurr = addDays(curr, 6);
                                    reqWeekPills.push({
                                        dateStr: wStr,
                                        label: `Semaine ${weekNum} (${format(curr, 'dd/MM')} - ${format(endCurr, 'dd/MM')})`
                                    });
                                    curr = addWeeks(curr, 1);
                                    weekNum++;
                                }
                            } catch (e) {}

                            return (
                                <div className="space-y-4">
                                    {/* Multi-week selector pills if request spans > 1 week */}
                                    {reqWeekPills.length > 1 && (
                                        <div className="flex items-center gap-2 overflow-x-auto pb-1 border-b border-border">
                                            <span className="text-xs font-bold text-white/60 whitespace-nowrap">Semaines de la demande :</span>
                                            {reqWeekPills.map(pill => {
                                                const isSelected = pill.dateStr === planningModalWeekStart;
                                                const isCreated = existingWeeks.length === 0 || existingWeeks.includes(pill.dateStr);
                                                return (
                                                    <button
                                                        key={pill.dateStr}
                                                        onClick={() => {
                                                            setPlanningModalWeekStart(pill.dateStr);
                                                            loadModalPlanningWeek(pill.dateStr);
                                                        }}
                                                        className={`text-xs font-bold px-3 py-1.5 rounded-xl transition-all whitespace-nowrap border ${
                                                            isSelected
                                                                ? 'bg-brand-600 text-brand-fg border-brand-500 shadow-lg shadow-brand-500/20'
                                                                : isCreated
                                                                ? 'bg-white/[0.03] hover:bg-white/10 text-white/60 border-border'
                                                                : 'bg-warning-500/10 text-warning-300 border-warning-500/20 hover:bg-warning-500/20'
                                                        }`}
                                                    >
                                                        {pill.label} {!isCreated && <span className="text-[9px] opacity-75 font-normal">(Non créé)</span>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Navigation Bar */}
                                    <div className="flex items-center justify-between bg-black/30 border border-border rounded-2xl p-3">
                                        <button
                                            onClick={() => hasPrevWeek && changeModalWeek(-1)}
                                            disabled={!hasPrevWeek}
                                            className={`flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-xl transition-all ${
                                                hasPrevWeek
                                                    ? 'text-white/60 hover:text-white bg-white/[0.03] hover:bg-white/10 border border-border'
                                                    : 'text-white/40 bg-white/[0.02] border border-border cursor-not-allowed opacity-70'
                                            }`}
                                            title={hasPrevWeek ? 'Semaine précédente' : 'La semaine précédente n\'a pas encore été créée'}
                                        >
                                            <ChevronLeft size={16} /> Semaine précédente
                                        </button>

                                        <div className="text-center">
                                            <p className="text-xs font-bold text-brand-500 dark:text-brand-600 dark:text-brand-400">
                                                Semaine du {planningModalWeekStart ? format(parseISO(planningModalWeekStart), 'dd MMMM yyyy', { locale: fr }) : ''} au {planningModalWeekStart ? format(addDays(parseISO(planningModalWeekStart), 6), 'dd MMMM yyyy', { locale: fr }) : ''}
                                            </p>
                                            {!isCurrentWeekCreated && (
                                                <p className="text-[10px] text-warning-400 font-semibold mt-0.5">Planning non créé sur la grille (Horaires par défaut affichés)</p>
                                            )}
                                        </div>

                                        <button
                                            onClick={() => hasNextWeek && changeModalWeek(1)}
                                            disabled={!hasNextWeek}
                                            className={`flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-xl transition-all ${
                                                hasNextWeek
                                                    ? 'text-white/60 hover:text-white bg-white/[0.03] hover:bg-white/10 border border-border'
                                                    : 'text-white/40 bg-white/[0.02] border border-border cursor-not-allowed opacity-70'
                                            }`}
                                            title={hasNextWeek ? 'Semaine suivante' : 'La semaine suivante n\'a pas encore été créée'}
                                        >
                                            Semaine suivante <ChevronRight size={16} />
                                        </button>
                                    </div>

                                    {/* Warning banner if week not created yet */}
                                    {!isCurrentWeekCreated && (
                                        <div className="bg-warning-500/10 border border-warning-500/20 rounded-2xl p-3 flex items-center justify-between text-xs text-warning-300">
                                            <div className="flex items-center gap-2">
                                                <AlertCircle size={16} className="text-warning-400 shrink-0" />
                                                <span>Ce planning n'a pas encore été créé sur la grille principale. Horaires par défaut affichés ci-dessous.</span>
                                            </div>
                                            <span className="text-[10px] font-black uppercase bg-warning-500/20 text-warning-400 px-2 py-0.5 rounded border border-warning-500/30 whitespace-nowrap">NON CRÉÉ</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Shift Row for Employee */}
                        {planningModalLoading ? (
                            <div className="py-16 text-center text-brand-600 dark:text-brand-400 flex flex-col items-center gap-2">
                                <Loader2 size={32} className="animate-spin" />
                                <span className="text-xs font-medium text-white/60">Chargement du planning...</span>
                            </div>
                        ) : (() => {
                            const empRow = (planningModalData?.rows || []).find((r: any) => String(r.employeeId) === String(planningModalReq.employe_id));
                            const weekStart = planningModalWeekStart ? parseISO(planningModalWeekStart) : new Date();
                            const dayNames = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

                            return (
                                <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
                                    {dayNames.map((dayName, idx) => {
                                        const dateObj = addDays(weekStart, idx);
                                        const dateStr = format(dateObj, 'yyyy-MM-dd');
                                        const isAbsenceOverlap = dateStr >= planningModalReq.date_start && dateStr <= planningModalReq.date_end;
                                        const savedShift = empRow?.shifts?.[dateStr];

                                        let statusBg = 'bg-white/[0.02] border-border';
                                        let statusText = 'REPOS';
                                        let badgeBg = 'bg-gray-500/10 text-white/50 border-gray-500/20';
                                        let badgeLabel = 'REPOS';

                                        if (savedShift && savedShift.type === 'travail' && Array.isArray(savedShift.segments) && savedShift.segments.length > 0) {
                                            statusBg = 'bg-emerald-500/5 border-emerald-500/20';
                                            badgeBg = 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
                                            badgeLabel = 'TRAVAIL';
                                            statusText = savedShift.segments.map((s: any) => s.start && s.end ? `${s.start} - ${s.end}` : s.label || 'Travail').join(' / ');
                                        } else if (savedShift && savedShift.type === 'absence') {
                                            statusBg = 'bg-orange-500/10 border-orange-500/30';
                                            badgeBg = 'bg-orange-500/20 text-orange-400 border-orange-500/30';
                                            badgeLabel = 'ABSENCE';
                                            const label = savedShift.segments?.[0]?.label || 'ABS';
                                            statusText = `Absence (${label})`;
                                        } else {
                                            // Fallback to weeklyDefaults from planningSettings
                                            const empIdStr = String(planningModalReq.employe_id);
                                            const defaultsMap = planningSettings?.weeklyDefaults?.[empIdStr] || {};
                                            const dayKeys = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
                                            const defKey = defaultsMap[String(idx)] || defaultsMap[dayKeys[idx]];

                                            if (defKey && defKey !== 'REPOS') {
                                                const templates = planningTemplates || [];
                                                const template = templates.find((t: any) => String(t.id) === String(defKey) || t.name === defKey);

                                                const slots = template?.slots || template?.shifts || [];
                                                if (slots.length > 0) {
                                                    statusBg = 'bg-emerald-500/5 border-emerald-500/20';
                                                    badgeBg = 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
                                                    badgeLabel = 'TRAVAIL';
                                                    statusText = slots.map((s: any) => `${s.start || s.startTime || ''} - ${s.end || s.endTime || ''}`).join(' / ');
                                                } else if (template?.name) {
                                                    statusBg = 'bg-emerald-500/5 border-emerald-500/20';
                                                    badgeBg = 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
                                                    badgeLabel = 'TRAVAIL';
                                                    statusText = template.name;
                                                }
                                            }
                                        }

                                        return (
                                            <div key={dateStr} className={`p-3 rounded-2xl border ${statusBg} flex flex-col justify-between transition-all ${isAbsenceOverlap ? 'ring-2 ring-brand-500/80 shadow-lg shadow-brand-500/20 bg-brand-500/5' : ''}`}>
                                                <div>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="text-[11px] font-black text-white/60 uppercase">{dayName}</span>
                                                        <span className="text-[10px] font-bold text-white/60">{format(dateObj, 'dd/MM')}</span>
                                                    </div>
                                                    <div className={`inline-block text-[10px] font-black uppercase px-2 py-0.5 rounded-md border ${badgeBg} mb-2`}>
                                                        {badgeLabel}
                                                    </div>
                                                    <p className="text-xs font-bold text-white break-words leading-snug">{statusText}</p>
                                                </div>
                                                {isAbsenceOverlap && (
                                                    <div className="mt-3 pt-2 border-t border-brand-500/30">
                                                        <span className="text-[9px] font-black uppercase text-brand-500 dark:text-brand-600 dark:text-brand-400 tracking-wider">Demande concernée</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}

                        <div className="flex items-center justify-end border-t border-border pt-4">
                            <button onClick={() => setPlanningModalReq(null)} className="px-5 py-2.5 bg-white/10 hover:bg-white/15 text-white rounded-xl text-xs font-bold transition-all">
                                Fermer
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};
